import React, { useEffect, useState } from 'react';
import { InfluencerInvoiceService } from './service';
import { ErrorToast } from '../../../utils/toasters';
import { Campaign } from '../../../model/campaign/Campaign';
import { useLocation, useNavigate } from 'react-router-dom';
import usePagination from '../../../shared/hooks/usePagination';
import { useTranslation } from 'react-i18next';
import InvoiceHeaderComponent from './components/InvoiceHeaderComponent';
import { Button, Col, Row } from 'react-bootstrap';
import { CustomAutocomplete } from '../../../shared/components/Autocomplete';
import InvoiceMainCard from './components/InvoiceMainCard';
import InvoiceSubCard from './components/InvoiceSubCard';
import InvoiceSubCardSkeleton from './components/InvoiceSubCardSkeleton';
import { InfluencerOfferModal } from '../offer/modal/offer-modal/InfluencerOfferModal';
import { InfluencerContractModal } from '../offer/modal/contract-modal/InfluencerContractModal';
import ModalUploadInvoice from './components/ModalUploadInvoice';
import { ConfirmationModal } from '../../../shared/components/modal/ConfirmationModal';
import EmptyState from '../../../shared/components/placeholder/EmptyState';
import { useInvoices } from '../../../shared/hooks/useInvoices';

export default function InfluencerInvoices() {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign>();

    const [showOfferModal, setShowOfferModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [openDropdown, setOpenDropdown] = React.useState(false);

    const { filterParams, setFilterParams } = usePagination({});
    const { t } = useTranslation();
    const { state }: any = useLocation();
    const navigate = useNavigate();

    const {
        uploadModalData,
        setUploadModalData,
        showDeleteModal,
        setShowDeleteModal,
        handleSubmitUpload,
        deleteInvoice,
        downloadInvoice,
        isUploading,
        isDeleting,
    } = useInvoices({
        t,
        filterParams,
        setFilterParams,
        selectedCampaign,
        setSelectedCampaign,
    });

    const campaignList = campaigns?.map(c => ({ ...c, offerId: c?.offers?.[0]?.id })) ?? [];

    useEffect(() => {
        InfluencerInvoiceService.getInvoiceCampaigns({ perPage: 0 })
            .then(response => {
                setCampaigns(response.data);

                if (state?.offerId) {
                    const found = response.data.find((el: Campaign) => el.offers?.[0]?.id === state.offerId);
                    setSelectedCampaign(found ?? null);
                    navigate(location.pathname, { replace: true });
                }
            })
            .catch(error => ErrorToast(error));
    }, [filterParams]);

    const selectCampaign = (params: any) => {
        setSelectedCampaign(() => campaignList?.find(c => c?.offerId === +params) || undefined);
        setOpenDropdown(false);
    };

    const navigateToArchive = (path: string) => {
        navigate(path);
    };

    const openUploadModal = (
        invoiceId: number,
        sequenceNumber: number,
        file?: string,
        rejectReason?: string,
        isLongTermOffer?: boolean,
        selectedCampaignId?: number,
        numberInvoices?: number
    ) => {
        setUploadModalData(prev => ({
            ...prev,
            show: true,
            sequenceNumber,
            invoiceId,
            invoiceFile: file,
            rejectReason,
            isLongTermOffer: selectedCampaign?.offers?.[0]?.isLongTerm,
            selectedCampaignId: selectedCampaign?.id,
            campaignTitle: selectedCampaign?.title,
            invoiceLength: numberInvoices,
        }));
    };

    const openModalAction = (isContractModal: boolean) => {
        isContractModal ? setShowContractModal(true) : setShowOfferModal(true);
    };

    const handleOpenDropdown = () => {
        setOpenDropdown(true);
    };

    const handleCloseDropdown = () => {
        setOpenDropdown(false);
    };

    return (
        <>
            <InfluencerOfferModal
                show={showOfferModal}
                setShowModal={setShowOfferModal}
                campaignId={String(selectedCampaign?.id ?? 0)}
                isLongTerm={selectedCampaign?.offers?.[0]?.isLongTerm ?? false}
            />

            <InfluencerContractModal
                show={showContractModal}
                setShowModal={setShowContractModal}
                campaignId={String(selectedCampaign?.id ?? 0)}
            />

            <ModalUploadInvoice
                closeModal={() =>
                    setUploadModalData({
                        show: false,
                        rejectReason: undefined,
                        sequenceNumber: undefined,
                        invoiceId: undefined,
                        invoiceFile: undefined,
                        isLongTermOffer: undefined,
                        selectedCampaignId: undefined,
                        campaignTitle: undefined,
                        invoiceLength: undefined,
                    })
                }
                rejectReason={uploadModalData?.rejectReason}
                invoiceFile={uploadModalData?.invoiceFile}
                show={uploadModalData.show}
                handleSubmit={handleSubmitUpload}
                isLoading={isUploading}
                sequenceNumber={uploadModalData?.sequenceNumber}
                invoiceLength={uploadModalData?.invoiceLength}
                campaignTitle={uploadModalData?.campaignTitle}
            />

            <ConfirmationModal
                title={t('influencer.invoice.modal.title')}
                description={t('influencer.invoice.modal.desc', {
                    campaignName: selectedCampaign?.title,
                })}
                action={deleteInvoice}
                show={showDeleteModal.show}
                closeModal={() => setShowDeleteModal(prev => ({ ...prev, show: false }))}
                isLoading={isDeleting}
            />

            <Row className="mb-5 pb-2">
                <InvoiceHeaderComponent />

                <Col xs={12} md={6} xl={3} className="mt-3 mb-md-5">
                    <CustomAutocomplete
                        dataArray={campaignList}
                        key={selectedCampaign?.offers?.[0]?.id}
                        isLongSelected={selectedCampaign?.offers?.[0]?.isLongTerm || selectedCampaign?.isLongTerm}
                        defaultArray={campaignList.find(c => c?.offerId === selectedCampaign?.offers?.[0].id) as any}
                        status={campaignList.find(c => c.id === selectedCampaign?.id)?.offers[0].status}
                        inputLabel="title"
                        isWithImage={false}
                        queryFilterKey="offerId"
                        disableClearable={false}
                        classes="pr-md-0"
                        inputLabelTranslation={t('general.campaign')}
                        handleChangeParams={(_, e) => selectCampaign(e)}
                        haveDots
                        isLongCampaignSelected={selectedCampaign?.isLongTerm}
                        isAdditionalOnlySelected={selectedCampaign?.offers?.[0]?.isAddonsOnly}
                        openDropdown={openDropdown}
                        handleOpenDropdown={handleOpenDropdown}
                        handleCloseDropdown={handleCloseDropdown}
                    />
                </Col>

                <Col className="mt-3 mb-5 text-end ">
                    <Button
                        className="w-md-auto w-100"
                        onClick={() => navigateToArchive('/influencer/invoices/archive')}
                        variant="outline-primary"
                    >
                        {t('influencer.invoice.uploaded_invoices')}
                    </Button>
                </Col>

                {selectedCampaign ? (
                    <Col className="col-12 mt-4">
                        <InvoiceMainCard
                            openDeleteModal={setShowDeleteModal}
                            downloadInvoice={downloadInvoice}
                            selectedCampaign={selectedCampaign}
                            openModalAction={openModalAction}
                            openUploadModal={openUploadModal}
                        />
                    </Col>
                ) : (
                    <EmptyState
                        desc={t('influencer.invoice.empty_desc')}
                        addClass="text-center"
                        title={t('influencer.invoice.empty_title')}
                        iconPath="/icons/profile/invoice.svg"
                        btnText={t('influencer.invoice.chose_campaign')}
                        func={handleOpenDropdown}
                        variant="primary"
                    />
                )}

                {selectedCampaign?.offers?.[0]?.invoiceSchedules?.map((invoice, i, arr) =>
                    invoice?.isAllowed ? (
                        <Col xs={12} key={invoice.id}>
                            <InvoiceSubCard
                                openUploadModal={openUploadModal}
                                invoice={invoice}
                                length={arr.length}
                                openDeleteModal={setShowDeleteModal}
                                downloadInvoice={downloadInvoice}
                                uploadBlocked={!!selectedCampaign?.offers?.[0]?.hasOpenAnnex}
                            />
                        </Col>
                    ) : (
                        <Col xs={12} key={invoice.id}>
                            <InvoiceSubCardSkeleton
                                length={arr.length}
                                dueDate={invoice?.dueDate}
                                sequenceNumber={invoice?.sequenceNumber}
                            />
                        </Col>
                    )
                )}
            </Row>
        </>
    );
}
